import React, { useCallback, useEffect, useMemo } from 'react'
import { Form, Row, Spin } from 'antd'
import { isEmpty } from 'lodash'

import {
  DirtyPromptForm,
  FormButtons,
  FormImageUpload,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'

import { useUpdateIngredientImageDetailsMutation } from '../../hooks'
import { ingredientPropType } from '../../prop-types'

import { ingredientImageFormInitialValues } from './initial-values'

const IngredientImageDetailsForm = ({ ingredient }) => {
  const [form] = Form.useForm()

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.UPDATE_IMAGE_DETAILS,
  ])

  const [
    updateIngredientImageDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientImageDetailsMutation()

  const initialValues = useMemo(
    () => ingredientImageFormInitialValues(ingredient),
    [ingredient],
  )

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const handleSaveData = useCallback(
    data => updateIngredientImageDetails(ingredient.id, data),
    [ingredient.id, updateIngredientImageDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [ingredient.imageUrl, resetForm])

  return isSaveLoading || isEmpty(initialValues) ? (
    <Spin />
  ) : (
    <>
      <DirtyPromptForm
        isDirty={isDirty}
        form={form}
        initialValues={initialValues}
        name="ingredientImageForm"
        onFinish={handleSaveData}
        autoComplete="off"
        onFieldsChange={setToDirty}
      >
        <Row>
          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="image"
            label="Image"
          />
        </Row>
        <FormButtons
          permissions={[PERMISSIONS.INGREDIENT.UPDATE_IMAGE_DETAILS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </DirtyPromptForm>
    </>
  )
}

IngredientImageDetailsForm.propTypes = {
  ingredient: ingredientPropType.isRequired,
}

export default IngredientImageDetailsForm
